import React, { useState } from "react";
import { Button } from "./Button";
import { Wand2, Loader2, RefreshCw } from "lucide-react";
import { aiDescriptionService } from "../../services/aiDescriptionService";
import { GlobalService } from "../../modules";
import { useAppDispatch } from "../../redux";

interface AIDescriptionGeneratorProps {
  title: string;
  serviceType?: string;
  requestType?: string;
  equipmentType?: string;
  propertyType?: string;
  onDescriptionGenerated: (description: string) => void;
  disabled?: boolean;
  className?: string;
}

const AIDescriptionGenerator: React.FC<AIDescriptionGeneratorProps> = ({
  title,
  serviceType,
  requestType,
  equipmentType,
  propertyType,
  onDescriptionGenerated,
  disabled = false,
  className = ""
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const dispatch = useAppDispatch();

  const handleGenerateDescription = async () => {
    if (!title.trim()) {
      GlobalService.showToast("warning", "Title Required", "Please enter a title first before generating description.");
      return;
    }

    setIsGenerating(true);
    setHasGenerated(false);

    try {
      const response = await aiDescriptionService.generateDescription({
        title: title.trim(),
        serviceType,
        requestType,
        equipmentType,
        propertyType
      }, dispatch);

      if (response.success && response.description) {
        onDescriptionGenerated(response.description);
        setHasGenerated(true);
        GlobalService.showToast("success", "Description Generated", "AI has generated a description based on your title.");
      } else {
        throw new Error(response.error || "Failed to generate description");
      }
    } catch (error) {
      console.error("Error generating description:", error);
      GlobalService.showToast("error", "Generation Failed", error instanceof Error ? error.message : "Failed to generate description. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        type="button"
        onClick={handleGenerateDescription}
        disabled={disabled || isGenerating || !title.trim()}
        variant={hasGenerated ? "outline" : "default"}
        className="flex items-center gap-2"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Generating...</span>
          </>
        ) : hasGenerated ? (
          <>
            <RefreshCw className="h-4 w-4" />
            <span>Regenerate</span>
          </>
        ) : (
          <>
            <Wand2 className="h-4 w-4" />
            <span>Generate with AI</span>
          </>
        )}
      </Button>
      
      {hasGenerated && (
        <span className="text-sm text-green-600 font-medium">
          ✓ AI Generated
        </span>
      )}
    </div>
  );
};

export default AIDescriptionGenerator;
